import { type AuthTokens } from "./types";
import { authService } from "./authService";

/** Child iframe → parent: request JWT stored on TechyWeb origin. */
export const TL4K_REQUEST_AUTH = "TL4K_REQUEST_AUTH" as const;

/** Parent → child iframe: hand off session tokens (same user, cross-origin storage). */
export const TL4K_AUTH_CREDENTIALS = "TL4K_AUTH_CREDENTIALS" as const;

export type Tl4kRequestAuthMessage = { type: typeof TL4K_REQUEST_AUTH };

export type Tl4kAuthCredentialsMessage = {
  type: typeof TL4K_AUTH_CREDENTIALS;
  accessToken: string;
  refreshToken: string;
};

export function isTl4kRequestAuthMessage(data: unknown): data is Tl4kRequestAuthMessage {
  return typeof data === "object" && data !== null && (data as Tl4kRequestAuthMessage).type === TL4K_REQUEST_AUTH;
}

export function isTl4kAuthCredentialsMessage(data: unknown): data is Tl4kAuthCredentialsMessage {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as Tl4kAuthCredentialsMessage).type === TL4K_AUTH_CREDENTIALS &&
    typeof (data as Tl4kAuthCredentialsMessage).accessToken === "string" &&
    typeof (data as Tl4kAuthCredentialsMessage).refreshToken === "string"
  );
}

/** Persist tokens received from parent frame and notify listeners. */
export function applySessionTokens(tokens: AuthTokens): void {
  authService.applySessionTokens(tokens);
}

/** True when loaded inside TechyWeb iframe (query flag or parent frame). */
export function isTl4kEmbedded(search?: string): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(search ?? window.location.search);
  if (params.get("tl4k-embedded") === "1") return true;
  try {
    return window.parent !== window;
  } catch {
    return false;
  }
}

export type WaitForEmbeddedAuthOptions = {
  timeoutMs?: number;
  pollIntervalMs?: number;
};

/**
 * Embedded child: wait until parent hands off tokens (or timeout).
 * Calls requestAuthFromParent once at start.
 */
export function waitForEmbeddedAuth(options?: WaitForEmbeddedAuthOptions): Promise<boolean> {
  const timeoutMs = options?.timeoutMs ?? 3000;
  const pollIntervalMs = options?.pollIntervalMs ?? 100;

  if (typeof window === "undefined" || window.parent === window) {
    return Promise.resolve(authService.isAuthenticated());
  }
  if (authService.isAuthenticated()) return Promise.resolve(true);

  requestAuthFromParent();

  return new Promise((resolve) => {
    const deadline = Date.now() + timeoutMs;
    let settled = false;

    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("tl4k-auth-updated", onAuth);
      window.clearInterval(poll);
      window.clearTimeout(timer);
      resolve(ok);
    };

    const onAuth = () => {
      if (authService.isAuthenticated()) finish(true);
    };

    window.addEventListener("tl4k-auth-updated", onAuth);

    const poll = window.setInterval(() => {
      if (authService.isAuthenticated()) finish(true);
      else if (Date.now() >= deadline) finish(false);
    }, pollIntervalMs);

    const timer = window.setTimeout(() => finish(authService.isAuthenticated()), timeoutMs);
  });
}

/** Ask TechyWeb parent for credentials (no-op when not embedded). */
export function requestAuthFromParent(targetOrigin = "*"): void {
  if (typeof window === "undefined" || window.parent === window) return;
  window.parent.postMessage({ type: TL4K_REQUEST_AUTH } satisfies Tl4kRequestAuthMessage, targetOrigin);
}

/**
 * Embedded app: listen for TL4K_AUTH_CREDENTIALS from parent.
 * Returns cleanup function.
 */
export function setupEmbeddedAuthListener(options?: { trustedOrigins?: string[] }): () => void {
  const trusted = options?.trustedOrigins;
  const handler = (event: MessageEvent) => {
    if (!isTl4kAuthCredentialsMessage(event.data)) return;
    if (trusted && trusted.length > 0 && !trusted.includes(event.origin)) {
      console.warn("[embeddedAuth] rejected credentials from untrusted origin", event.origin);
      return;
    }
    applySessionTokens({
      accessToken: event.data.accessToken,
      refreshToken: event.data.refreshToken,
    });
  };
  window.addEventListener("message", handler);
  // Chiedi subito i token al parent: i push proattivi del parent (0/200/800ms)
  // possono andare persi se arrivano prima del mount di questo listener, e token
  // stantii in localStorage farebbero saltare la richiesta lazy in waitForEmbeddedTokens.
  requestAuthFromParent();
  return () => window.removeEventListener("message", handler);
}

/** Parent shell: post current session tokens to an embedded iframe window. */
export function postAuthCredentialsToWindow(target: Window, targetOrigin = "*"): boolean {
  const tokens = authService.getSessionTokens();
  if (!tokens) return false;
  target.postMessage(
    {
      type: TL4K_AUTH_CREDENTIALS,
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
    } satisfies Tl4kAuthCredentialsMessage,
    targetOrigin
  );
  return true;
}
